// Everything that happened to this project's model, in order.
//
// Investigating an update used to mean hopping between three pages -
// Revisions for diffs, Export history for exports, the Review Center for
// resolutions - with nothing putting them on the same spine. The
// question people actually ask is "what happened to this model, and in
// what order", and a resolution pushed at 14:02 only means something
// when you can see the export before it and the commit after it.
//
// Every entry that has a commit behind it can be opened as the model at
// that moment, with its own diff painted on - one at a time, on demand.
import React, { useEffect, useMemo, useState } from "react";
import { Segmented, Tag, Empty, Skeleton } from "antd";
import moment from "moment";
import { url, tokenRef } from "api";
import TimepointModelPane from "components/Coordination/TimepointModelPane";
import GeomDelta from "components/Coordination/GeomDelta";

const KIND = {
  commit: { label: "Model push", color: "#1F4E63", dot: "#1F4E63" },
  "resolution-push": { label: "Resolution push", color: "#6D28D9", dot: "#6D28D9" },
  resolution: { label: "Resolution", color: "#0F766E", dot: "#0F766E" },
  export: { label: "Export", color: "#B45309", dot: "#B45309" },
};

export default function UnifiedModelTimeline({ projectId }) {
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState({});
  const [only, setOnly] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!projectId) return;
    setRows(null);
    const t = localStorage.getItem(tokenRef);
    fetch(`${url}/projects/${projectId}/model-timeline`, {
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.err) { setErr(d.err); setRows([]); return; }
        setRows(d.entries || []);
        setCounts(d.counts || {});
      })
      .catch((e) => { setErr(e.message || "Could not load the timeline"); setRows([]); });
  }, [projectId]);

  const shown = useMemo(() => {
    if (!rows) return [];
    if (only === "all") return rows;
    if (only === "resolution") return rows.filter((r) => r.kind === "resolution" || r.kind === "resolution-push");
    return rows.filter((r) => r.kind === only);
  }, [rows, only]);

  if (rows === null) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (err) return <Empty description={err} style={{ padding: 40 }} />;
  if (!rows.length) return <Empty description="Nothing has happened to this model yet" style={{ padding: 40 }} />;

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Segmented
          size="small"
          value={only}
          onChange={setOnly}
          options={[
            { value: "all", label: `Everything (${counts.all || 0})` },
            { value: "commit", label: `Model pushes (${counts.commit || 0})` },
            { value: "resolution", label: `Resolutions (${counts.resolution || 0})` },
            { value: "export", label: `Exports (${counts.export || 0})` },
          ]}
        />
      </div>

      <div style={{ position: "relative", paddingLeft: 26 }}>
        <div style={{ position: "absolute", left: 11, top: 8, bottom: 8, width: 2, background: "#e8edf1" }} />
        {shown.map((e) => {
          const k = KIND[e.kind] || KIND.commit;
          const c = e.counts || {};
          // Anything with a commit behind it gets the git-shaped counts,
          // zero or not: a push that changed nothing is a finding, and
          // hiding the row makes it look like missing data instead.
          const hasChange = !!e.commitId;
          return (
            <div key={e.id} style={{ position: "relative", paddingBottom: 14 }}>
              <div
                style={{
                  position: "absolute", left: -21, top: 14, width: 14, height: 14,
                  borderRadius: "50%", background: k.dot, border: "3px solid #fff",
                  boxShadow: "0 0 0 1px #e8edf1",
                }}
              />
              <div style={{ background: "#fff", border: "1px solid #e8edf1", borderRadius: 8, padding: "10px 14px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <Tag color={k.color} style={{ margin: 0 }}>{k.label}</Tag>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#20303C" }}>{e.title}</span>
                  <span style={{ fontSize: 11, color: "#7C8B96" }}>
                    {moment(e.at).format("MMM D, YYYY · h:mm A")}
                    {e.by ? ` · ${e.by}` : ""}
                  </span>
                  {e.reviewStatus === "pending" && <Tag color="orange" style={{ margin: 0 }}>awaiting approval</Tag>}
                  {e.status && e.kind === "resolution" && <Tag style={{ margin: 0 }}>{e.status}</Tag>}
                  <span style={{ flex: 1 }} />
                  {e.commitId && (
                    <button
                      onClick={() => setOpenId(openId === e.id ? null : e.id)}
                      style={{
                        background: "transparent", border: "1px solid #cfd8de", color: "#1F4E63",
                        padding: "3px 10px", borderRadius: 4, fontSize: 11.5, cursor: "pointer",
                      }}
                    >
                      {openId === e.id ? "Hide model" : "View model at this point"}
                    </button>
                  )}
                </div>

                {hasChange ? (
                  <div style={{ display: "flex", gap: 10, fontSize: 12, marginTop: 6, alignItems: "center", flexWrap: "wrap" }}>
                    <GeomDelta
                      added={c.added || 0}
                      modified={c.modified || 0}
                      removed={c.removed || 0}
                      inferred={!!e.countsAreInferred}
                    />
                    {e.scope === "selection" && <Tag style={{ margin: 0, fontSize: 10 }}>selection</Tag>}
                    {e.issueCount > 0 && (
                      <span style={{ fontSize: 11, color: "#7C8B96" }}>
                        for {e.issueCount} issue{e.issueCount === 1 ? "" : "s"}
                      </span>
                    )}
                    {/* The numbers say how many; this says what. Both
                        come from the stored diff, so the sentence can
                        never disagree with the counts beside it. */}
                    {e.narrative?.text && (
                      <span style={{ fontSize: 12, color: "#42606F", fontWeight: 600, lineHeight: 1.4 }}>
                        {e.narrative.text}
                        {e.narrative.approx && (
                          <span style={{ color: "#9AA7B0", fontWeight: 400, marginLeft: 6, fontSize: 11 }}>
                            (approximate until re-exported)
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                ) : null}

                {e.note && (
                  <div style={{ fontSize: 12, color: "#526470", marginTop: 6 }}>{e.note}</div>
                )}

                {openId === e.id && (
                  <div style={{ marginTop: 10 }}>
                    <TimepointModelPane
                      projectId={projectId}
                      commitId={e.commitId}
                      label={e.title}
                      height={380}
                      autoLoad
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
